import { formatDetailsFeature } from '~app/pages/Pricing/lib/packageFeatures';
import { Feature } from '~shared/types/pricing/pricingsvc';
import {
  DetailsFeatureCategoryGroups,
  DetailsFeatureSets,
  DetailsFeatureValueType,
  DetailsFeatureRow,
  DetailsFeatureRowFeature,
  FeaturesCategoryGroup,
} from './types';

import { PackageDataSet } from '~shared/types/pricing/package';
import { FeatureSetMap, FeatureMapItem } from '../types';
import { convertFeatureSetToMap } from '~pricing/lib/lib-helpers';
import COPY from '~pricing/lib/packageFeatures/details/copy';
import { getFormattedAmountValue } from './helpers';
import detailsFeatureRules from '~pricing/transformers/details/detailsFeatureRules';
import fakeFeatures from './marketingFeatures';
import combinedFeatures from './combinedFeatures';

/**
   * Returns an array with the following structure
   * [
    {
      name: 'Team Collaboration',
      features: [
        {
          feature: 'team_themes',
          title: 'Shared themes',
          packages: [
            {
              packageName: 'TEAM_ADVANTAGE_ANNUAL',
              value: true,
            },
            {
              packageName: 'TEAM_PREMIER_ANNUAL',
              value: true,
            },
            {
              packageName: 'ENT_PLATINUM',
              value: true,
            },
          ],
        },
        {
          feature: 'group_templates',
          title: 'Shared templates',
          packages: [
            {
              packageName: 'TEAM_ADVANTAGE_ANNUAL',
              value: false,
            },
            {
              packageName: 'TEAM_PREMIER_ANNUAL',
              value: true,
            },
            {
              packageName: 'ENT_PLATINUM',
              value: true,
            },
          ],
        },
      ],
    },
    {
      name: 'Survey Capabilities',
      features: [
        {
          feature: 'survey_create_limit',
          title: 'Number of surveys',
          packages: [
            {
              packageName: 'TEAM_ADVANTAGE_ANNUAL',
              value: 'UNLIMITED',
            },
            {
              packageName: 'TEAM_PREMIER_ANNUAL',
              value: 'UNLIMITED',
            },
            {
              packageName: 'ENT_PLATINUM',
              value: 'UNLIMITED',
            },
          ],
        },
      ],
    },
  ];
  */

function getFeatureValue(packageName: string, feature: Feature, locale: string): DetailsFeatureValueType {
  if (feature?.amount) {
    return detailsFeatureRules[feature?.name]
      ? detailsFeatureRules[feature.name](packageName, feature, { locale })
      : getFormattedAmountValue(feature, { locale });
  }

  if (feature?.setting) {
    return detailsFeatureRules[feature?.name]
      ? detailsFeatureRules[feature.name](packageName, feature)
      : feature?.setting;
  }

  return detailsFeatureRules[feature?.name] ? detailsFeatureRules[feature.name](packageName) : feature?.isEnabled;
}

function determineCombinedFeature(
  packageName: string,
  packageFeatures: FeatureSetMap,
  feature: string,
  locale: string
): DetailsFeatureValueType {
  const combinedFeature: FeatureMapItem =
    combinedFeatures.find(combined => combined.features.includes(feature)) ?? null;
  if (combinedFeature) {
    const features = combinedFeature?.features?.reduce<FeatureSetMap>((acc, key) => {
      acc[key] = packageFeatures[key];
      return acc;
    }, {});
    return features ? detailsFeatureRules[combinedFeature.featureItemKey](packageName, features, { locale }) : null;
  }
  return null;
}

export default function getFeatureCategories(
  displayedPackages: PackageDataSet,
  groupings: DetailsFeatureCategoryGroups,
  locale: string
): DetailsFeatureSets {
  const transformedPackages = displayedPackages.map(pkg => {
    return {
      ...pkg,
      features: convertFeatureSetToMap([...pkg.features, ...fakeFeatures]),
    };
  });

  return groupings.map((group: FeaturesCategoryGroup): DetailsFeatureRow => {
    return {
      name: group.category,
      features: group.features?.map((feature: string): DetailsFeatureRowFeature => {
        return {
          ...formatDetailsFeature(feature as keyof typeof COPY),
          packages: transformedPackages.map(pkg => {
            return {
              packageName: pkg.packageName,
              value:
                determineCombinedFeature(pkg.packageName, pkg.features, feature, locale) ??
                getFeatureValue(pkg.packageName, pkg.features[feature] || feature, locale),
            };
          }),
        };
      }),
    };
  });
}
